import React from "react";
import { Alert, InputNumber, Select, Spin, Switch } from "antd";

import useCoreBankingTenantInfo from "../../../../custom-hooks/useCoreBankingTenant";
import useMifosProduct from "../../../../custom-hooks/useMifosProduct";
import { useWalletProviders } from "../../../../custom-hooks/useWallet";
import { useDepositTypes } from "../../../../custom-hooks/useDepositTypes";
import useInvestmentInterestTypes from "../../../../custom-hooks/useInvestmentInterestType";
import { useGetTermsAndConditions } from "../../../DigitalBank/InvestmentTermsAndConditions/Hooks/useTermsAndCondtions";
import {
  hasCoreBankingLimits,
  hasInterestRateChart,
} from "../../../../helpers/depositType";
import { TInterestRateChart } from "../../../../helpers/interestRateChart";
import { TInvestmentProductDetails } from "../CreateInvestmentProduct";
import Field from "./Field";
import Notice from "./Notice";
import { MONEY_INPUT } from "./money";
import RateChartSection, { emptyInterestRateChart } from "./RateChartSection";
import styles from "./fundForm.module.scss";

type TLocks = Partial<Record<keyof TInvestmentProductDetails, string>>;

interface ICurrencyDetailSection {
  detail: TInvestmentProductDetails;
  onChange: (patch: Partial<TInvestmentProductDetails>) => void;
  locks?: TLocks;
  /**
   * Whether this fund is live.
   *
   * Three things follow from it, and they used to be three separate flags all
   * passed the same value: locked fields explain themselves, the limits stay
   * typeable even for a deposit type that inherits them (a live fund's limits
   * govern new subscriptions, not the accounts already open), and the fund can
   * be locked to new money.
   */
  isLive?: boolean;
}

const PAGE_SIZE = 100;

/**
 * One currency's setup: where the money sits, and what it earns.
 *
 * Two things here are deposit-type dependent, and both used to be wrong for a
 * savings deposit. The limits come from the core banking product for fixed and
 * recurring, but a Mifos savings account carries neither - so for savings the
 * admin types them. And a rate chart is required for savings and rejected for
 * everything else, so the section below appears for exactly one deposit type.
 */
export default function CurrencyDetailSection({
  detail,
  onChange,
  locks = {},
  isLive = false,
}: ICurrencyDetailSection) {
  const { data: walletProviders } = useWalletProviders();
  const { data: coreBankingTenant } = useCoreBankingTenantInfo();
  const { data: depositTypes } = useDepositTypes();
  const { data: interestTypes } = useInvestmentInterestTypes();
  const { data: termsAndConditions } = useGetTermsAndConditions({
    params: { page: 1, pageSize: PAGE_SIZE },
  });

  const {
    data: mifosProducts,
    isLoading: loadingProducts,
    isError: productsFailed,
    refetch: retryProducts,
  } = useMifosProduct({
    productType: detail?.depositType,
    tenantId: detail?.coreBankingTenantId,
  });

  const currency = detail?.currencyCode || detail?.value || "";
  const limitsFromProduct =
    hasCoreBankingLimits(detail?.depositType) && !isLive;
  const showChart = hasInterestRateChart(detail?.depositType);

  const productsForCurrency = (mifosProducts ?? []).filter(
    (product) => product.currencyCode === currency,
  );

  const lockFor = (field: keyof TInvestmentProductDetails) =>
    isLive ? locks[field] : undefined;

  /*
   * The API has sent this back as the string "true" as well as a boolean, and
   * the old screen ran `JSON.parse` on it — which threw outright when the
   * field was absent. Read defensively, write a boolean.
   */
  const isFundLocked = detail?.isLocked === true || detail?.isLocked === "true";

  const limitHint = limitsFromProduct
    ? "From the core banking product."
    : isLive
      ? "Applies to new subscriptions only."
      : undefined;

  return (
    <div className={styles.sectionBody}>
      <div className={styles.grid}>
        <Field
          required
          label="External wallet provider"
          lockedReason={lockFor("externalWalletProvider")}
        >
          <Select
            size="large"
            style={{ width: "100%" }}
            placeholder="Select a provider"
            disabled={Boolean(lockFor("externalWalletProvider"))}
            value={
              detail?.externalWalletProvider
                ? Number(detail.externalWalletProvider)
                : undefined
            }
            onChange={(next) =>
              onChange({ externalWalletProvider: Number(next) })
            }
            options={(walletProviders ?? []).map((provider: any) => ({
              value: provider.id,
              label: provider.name,
            }))}
          />
        </Field>

        <Field
          required
          label="Core banking tenant"
          lockedReason={lockFor("coreBankingTenantId")}
        >
          <Select
            size="large"
            style={{ width: "100%" }}
            placeholder="Select a tenant"
            disabled={Boolean(lockFor("coreBankingTenantId"))}
            value={detail?.coreBankingTenantId || undefined}
            onChange={(next) =>
              onChange({
                coreBankingTenantId: String(next),
                // The product list is scoped to the tenant, so the previous
                // choice no longer belongs to it.
                coreBankingProductId: undefined as unknown as number,
              })
            }
            options={(coreBankingTenant?.data ?? []).map((tenant: any) => ({
              value: tenant.tenant_id,
              label: tenant.full_name,
            }))}
          />
        </Field>

        <Field
          required
          label="Deposit type"
          hint={
            showChart
              ? "Savings deposits carry their own rate chart."
              : "Rates come from the core banking product."
          }
          lockedReason={lockFor("depositType")}
        >
          <Select
            size="large"
            style={{ width: "100%" }}
            placeholder="Select a deposit type"
            disabled={Boolean(lockFor("depositType"))}
            value={detail?.depositType ? Number(detail.depositType) : undefined}
            onChange={(next) =>
              onChange({
                depositType: Number(next),
                // Everything below belonged to the product chosen for the
                // previous deposit type.
                coreBankingProductId: undefined as unknown as number,
                minimumInvestment: undefined as unknown as number,
                maximumInvestment: undefined as unknown as number,
              })
            }
            options={(depositTypes ?? []).map((type: any) => ({
              value: type.id,
              label: type.name,
            }))}
          />
        </Field>

        <Field
          required
          label="Core banking product"
          lockedReason={lockFor("coreBankingProductId")}
        >
          <Select
            size="large"
            style={{ width: "100%" }}
            placeholder={
              detail?.coreBankingTenantId
                ? "Select a product"
                : "Choose a tenant first"
            }
            disabled={
              Boolean(lockFor("coreBankingProductId")) ||
              !detail?.coreBankingTenantId ||
              !detail?.depositType
            }
            loading={loadingProducts}
            notFoundContent={
              loadingProducts ? (
                <div style={{ padding: 12, textAlign: "center" }}>
                  <Spin size="small" />
                </div>
              ) : (
                <div style={{ padding: 12, color: "#8b8699" }}>
                  No {currency} products for this tenant
                </div>
              )
            }
            value={detail?.coreBankingProductId || undefined}
            onChange={(next) => {
              const picked = productsForCurrency.find(
                (product) => product.id === Number(next),
              );

              onChange({
                coreBankingProductId: Number(next),
                currencyCode: picked?.currencyCode ?? currency,
                // Null for savings — the fields below stay open for typing.
                minimumInvestment: picked?.minimumInvestment ?? undefined,
                maximumInvestment: picked?.maximumInvestment ?? undefined,
              });
            }}
            options={productsForCurrency.map((product) => ({
              value: product.id,
              label: `${product.currencyCode} · ${product.name}`,
            }))}
          />
        </Field>

        {productsFailed ? (
          <Alert
            className={styles.span2}
            type="error"
            showIcon
            message="Could not load core banking products"
            description="The list is empty because the request failed, not because there are none."
            action={
              <button
                type="button"
                className={styles.segment}
                onClick={() => retryProducts()}
              >
                Try again
              </button>
            }
          />
        ) : null}

        {!limitsFromProduct && detail?.depositType ? (
          <Notice tone="warn" className={styles.span2}>
            A savings product in core banking carries no deposit limits, so
            enter them here. Fixed and recurring deposits inherit theirs from
            the product.
          </Notice>
        ) : null}

        <Field
          required
          label="Minimum investment"
          lockedReason={lockFor("minimumInvestment")}
          hint={limitHint}
        >
          <InputNumber
            size="large"
            style={{ width: "100%" }}
            prefix={currency === "NGN" ? "₦" : currency}
            {...MONEY_INPUT}
            min={0}
            placeholder={limitsFromProduct ? "" : "Enter a minimum"}
            disabled={
              limitsFromProduct || Boolean(lockFor("minimumInvestment"))
            }
            value={detail?.minimumInvestment ?? undefined}
            onChange={(next) =>
              onChange({
                minimumInvestment:
                  next === null
                    ? (undefined as unknown as number)
                    : Number(next),
              })
            }
          />
        </Field>

        <Field
          required
          label="Maximum investment"
          lockedReason={lockFor("maximumInvestment")}
          hint={limitHint}
        >
          <InputNumber
            size="large"
            style={{ width: "100%" }}
            prefix={currency === "NGN" ? "₦" : currency}
            {...MONEY_INPUT}
            min={0}
            placeholder={limitsFromProduct ? "" : "Enter a maximum"}
            disabled={
              limitsFromProduct || Boolean(lockFor("maximumInvestment"))
            }
            value={detail?.maximumInvestment ?? undefined}
            onChange={(next) =>
              onChange({
                maximumInvestment:
                  next === null
                    ? (undefined as unknown as number)
                    : Number(next),
              })
            }
          />
        </Field>

        <Field required label="Interest payment type">
          <Select
            size="large"
            style={{ width: "100%" }}
            placeholder="Select"
            value={detail?.investmentInterestPaymentTypeId || undefined}
            onChange={(next) =>
              onChange({ investmentInterestPaymentTypeId: Number(next) })
            }
            options={(interestTypes?.data ?? []).map((type: any) => ({
              value: type.id,
              label: type.name,
            }))}
          />
        </Field>

        <Field required label="Terms and conditions">
          <Select
            size="large"
            style={{ width: "100%" }}
            placeholder="Select"
            showSearch
            optionFilterProp="label"
            value={detail?.termsAndConditionId || undefined}
            onChange={(next) => onChange({ termsAndConditionId: Number(next) })}
            options={(termsAndConditions?.data ?? []).map((item: any) => ({
              value: item.id,
              label: item.title,
            }))}
          />
        </Field>

        <div className={`${styles.span2} ${styles.rule}`} />

        <div className={`${styles.span2} ${styles.groupLabel}`}>
          Early liquidation penalties
        </div>

        <Field required label="Monthly payout rate">
          <InputNumber
            size="large"
            style={{ width: "100%" }}
            suffix="%"
            min={0}
            max={100}
            step={0.5}
            value={detail?.monthlyPrematureLiquidationRate ?? undefined}
            onChange={(next) =>
              onChange({ monthlyPrematureLiquidationRate: Number(next ?? 0) })
            }
          />
        </Field>

        <Field required label="End of tenor rate">
          <InputNumber
            size="large"
            style={{ width: "100%" }}
            suffix="%"
            min={0}
            max={100}
            step={0.5}
            value={detail?.endOfTenurePrematureLiquidationRate ?? undefined}
            onChange={(next) =>
              onChange({
                endOfTenurePrematureLiquidationRate: Number(next ?? 0),
              })
            }
          />
        </Field>

        {isLive ? (
          <div className={styles.span2}>
            <div className={`${styles.span2} ${styles.rule}`} />

            <Field
              label={`Lock ${currency} to new money`}
              hint="Existing plans keep running either way. Locking only stops new subscriptions in this currency."
            >
              <div className={styles.lockToggle}>
                <Switch
                  aria-label={`Lock ${currency} to new money`}
                  checked={isFundLocked}
                  onChange={(locked) => onChange({ isLocked: locked })}
                />
                <span className={isFundLocked ? styles.lockOn : styles.lockOff}>
                  {isFundLocked ? "Locked" : "Open"}
                </span>
              </div>
            </Field>
          </div>
        ) : null}
      </div>

      {showChart ? (
        <div style={{ marginTop: 24 }}>
          <RateChartSection
            currencyCode={currency}
            value={
              detail?.interestRateChart ??
              emptyInterestRateChart(`${currency} Savings Rate Chart`.trim())
            }
            onChange={(interestRateChart: TInterestRateChart) =>
              onChange({ interestRateChart })
            }
          />
        </div>
      ) : null}
    </div>
  );
}
